import React, { useMemo, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Container,
    Stack,
    Typography,
} from '@mui/material'
import api from '../services/api'

const DIASPORA_CODES = ['US', 'CA', 'UK', 'GB', 'AU', 'FR', 'EU']
const rates = {
    USD: 1,
    GBP: 1.27,
    CAD: 0.74,
    AUD: 0.66,
    EUR: 1.09,
    SLE: 0.044,
    LRD: 0.0051,
    GNF: 0.00012,
    GMD: 0.014,
}

const money = (amount, currency) => {
    try {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency,
        }).format(Number(amount || 0))
    } catch {
        return `${currency} ${Number(amount || 0).toFixed(2)}`
    }
}

const countryCodeFromCurrency = (currency) => {
    switch (String(currency || '').toUpperCase()) {
        case 'LRD':
            return 'LR'
        case 'GNF':
            return 'GN'
        case 'GMD':
            return 'GM'
        case 'SLE':
        default:
            return 'SL'
    }
}

const isEzWakaReference = (value) => String(value || '').toUpperCase().startsWith('WKG-PAY-')

const PaymentConfirm = () => {
    const navigate = useNavigate()
    const { state } = useLocation()
    const provider = state?.provider
    const amount = state?.amount
    const codeFlow = Boolean(state?.codeFlow)
    const codeRecord = state?.codeRecord
    const paymentProfile = state?.paymentProfile
    const residency = state?.residency || 'DIASPORA'
    const residentCountry = state?.residentCountry || 'US'

    const [loading, setLoading] = useState(false)

    const savedUser = JSON.parse(localStorage.getItem('session_user') || '{}')
    const senderCurrency = String(savedUser?.baseCurrency || 'USD').toUpperCase()
    const isDiaspora =
        residency === 'DIASPORA' || DIASPORA_CODES.includes(String(residentCountry).toUpperCase())
    const settlementAmount = codeFlow ? Number(codeRecord?.amount || 0) : Number(amount || 0)
    const settlementCurrency = String(
        (codeFlow ? codeRecord?.localCurrency : provider?.payoutCurrency) || 'USD'
    ).toUpperCase()

    const pricing = useMemo(() => {
        if (!settlementAmount) return { base: 0, fee: 0, total: 0, rate: 1 }
        if (!isDiaspora) {
            const fee = Number((Math.max(settlementAmount * 0.006, 2)).toFixed(2))
            return { base: settlementAmount, fee, total: Number((settlementAmount + fee).toFixed(2)), rate: 1 }
        }
        const usdValue = settlementAmount * (rates[settlementCurrency] || 1)
        const base = Number((usdValue / (rates[senderCurrency] || 1)).toFixed(2))
        const fee = Number((Math.max(base * 0.018, 1.25)).toFixed(2))
        return {
            base,
            fee,
            total: Number((base + fee).toFixed(2)),
            rate: Number((settlementAmount / Math.max(base, 1)).toFixed(2)),
        }
    }, [isDiaspora, senderCurrency, settlementAmount, settlementCurrency])

    if ((!provider || !amount) && !codeFlow) {
        navigate('/payment')
        return null
    }

    if (codeFlow && !codeRecord) {
        navigate('/payment')
        return null
    }

    const handlePayment = async () => {
        try {
            setLoading(true)

            if (codeFlow) {
                const invoiceCode = codeRecord?.invoiceNumber || codeRecord?.code
                const paymentReference = codeRecord?.paymentReference || invoiceCode
                const ezwaka = isEzWakaReference(invoiceCode)

                await api.post('/service-payments/pay', {
                    payment_category: 'invoice_payment',
                    destination_type: isDiaspora ? 'merchant' : 'mojopayr_merchant',
                    destination_name: codeRecord.merchantName || 'MojoPayr Merchant',
                    destination_country:
                        codeRecord.destinationCountry || countryCodeFromCurrency(settlementCurrency),
                    amount: pricing.base,
                    sender_currency: senderCurrency,
                    receiver_currency: settlementCurrency,
                    payout_method: 'service',
                    provider: codeRecord.merchantName || 'MojoPayr Merchant',
                    account_number: codeRecord.accountNumber || invoiceCode,
                    payment_reference: paymentReference,
                    invoice_number: invoiceCode,
                    stays_inside_mojopayr: !isDiaspora,
                    metadata: {
                        ...(ezwaka ? { source: 'ezwaka' } : {}),
                        invoice_title: codeRecord.description || 'Invoice payment',
                        customer_name: codeRecord.customerName || 'Customer',
                        invoice_amount: settlementAmount,
                        invoice_currency: settlementCurrency,
                        public_token: codeRecord.publicToken || null,
                    },
                })
            } else if (!codeFlow) {
                await api.post('/payment/process', {
                    provider_id: provider.id,
                    amount,
                })
            }

            navigate('/transactions')
        } catch (err) {
            const message =
                err?.response?.data?.message ||
                err?.response?.data?.errors?.payment?.[0] ||
                'Payment failed'
            alert(message)
        } finally {
            setLoading(false)
        }
    }

    const title = codeFlow ? 'Payment Code Review' : 'Confirm Payment'
    const targetName = codeFlow ? codeRecord.merchantName : provider.business_name || provider.name
    const targetLocation = codeFlow ? codeRecord.merchantLocation : provider.city
    const descriptor = codeFlow
        ? codeRecord.description
        : paymentProfile?.descriptor || `${provider.category} payment`

    return (
        <Box sx={{ minHeight: '100vh', py: 6, bgcolor: '#09061B' }}>
            <Container maxWidth="sm">
                <Card
                    sx={{
                        borderRadius: 6,
                        mb: 3,
                        overflow: 'hidden',
                        background:
                            'linear-gradient(135deg, rgba(20,11,48,0.98) 0%, rgba(77,41,202,0.95) 58%, rgba(255,138,0,0.16) 100%)',
                        border: '1px solid rgba(255,255,255,0.08)',
                    }}
                >
                    <CardContent sx={{ p: 4 }}>
                        <Chip
                            label={codeFlow ? 'Code matched' : 'Payment review'}
                            sx={{
                                mb: 2,
                                bgcolor: 'rgba(255,255,255,0.12)',
                                color: '#fff',
                                fontWeight: 800,
                            }}
                        />
                        <Typography sx={{ color: '#fff', fontWeight: 950, fontSize: 36 }}>
                            {title}
                        </Typography>
                        <Typography sx={{ color: 'rgba(255,255,255,0.80)', mt: 1 }}>
                            {codeFlow
                                ? 'MojoPayr matched your code and opened the payment review, just like the mobile app flow.'
                                : 'Review the payment details before confirming.'}
                        </Typography>
                    </CardContent>
                </Card>

                <Card
                    sx={{
                        borderRadius: 6,
                        bgcolor: 'rgba(16,11,38,0.92)',
                        border: '1px solid rgba(255,255,255,0.08)',
                    }}
                >
                    <CardContent sx={{ p: 3 }}>
                        <Stack spacing={2}>
                            <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: 22 }}>
                                {targetName}
                            </Typography>
                            <Typography sx={{ color: 'rgba(255,255,255,0.64)' }}>
                                {targetLocation} • {descriptor}
                            </Typography>
                            {codeFlow ? (
                                <Typography sx={{ color: '#FF8A00', fontWeight: 800 }}>
                                    {codeRecord.type === 'invoice' ? 'Invoice code' : 'Payment code'}: {codeRecord.code}
                                </Typography>
                            ) : null}

                            <Stack spacing={1.2} sx={{ mt: 1 }}>
                                <SummaryRow
                                    label={`Merchant receives (${settlementCurrency})`}
                                    value={money(settlementAmount, settlementCurrency)}
                                />
                                <SummaryRow
                                    label={isDiaspora ? `You pay (${senderCurrency})` : `You pay (${settlementCurrency})`}
                                    value={money(pricing.base, isDiaspora ? senderCurrency : settlementCurrency)}
                                />
                                <SummaryRow
                                    label="Fee"
                                    value={money(pricing.fee, isDiaspora ? senderCurrency : settlementCurrency)}
                                />
                                <SummaryRow
                                    label="Total"
                                    value={money(pricing.total, isDiaspora ? senderCurrency : settlementCurrency)}
                                    strong
                                />
                            </Stack>

                            <Box
                                sx={{
                                    p: 2,
                                    borderRadius: 4,
                                    bgcolor: 'rgba(255,138,0,0.08)',
                                    border: '1px solid rgba(255,138,0,0.16)',
                                }}
                            >
                                <Typography sx={{ color: '#fff', fontWeight: 800, mb: 0.6 }}>
                                    MojoPayr logic
                                </Typography>
                                <Typography sx={{ color: 'rgba(255,255,255,0.72)', fontSize: 13.5 }}>
                                    {isDiaspora
                                        ? `Diaspora payments use remittance-style FX and fees. Rate: 1 ${senderCurrency} = ${pricing.rate.toFixed(2)} ${settlementCurrency}`
                                        : `Local payments stay simpler and cheaper to encourage daily usage volume.`}
                                </Typography>
                            </Box>

                            <Button
                                variant="contained"
                                onClick={handlePayment}
                                disabled={loading}
                                sx={{
                                    mt: 1,
                                    borderRadius: 999,
                                    py: 1.4,
                                    textTransform: 'none',
                                    fontWeight: 900,
                                    bgcolor: '#FF8A00',
                                    '&:hover': { bgcolor: '#ff9c2a' },
                                }}
                            >
                                {loading ? 'Processing...' : 'Confirm & Pay'}
                            </Button>
                        </Stack>
                    </CardContent>
                </Card>
            </Container>
        </Box>
    )
}

const SummaryRow = ({ label, value, strong = false }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
        <Typography sx={{ color: 'rgba(255,255,255,0.62)', fontWeight: 700 }}>
            {label}
        </Typography>
        <Typography sx={{ color: '#fff', fontWeight: strong ? 900 : 800 }}>
            {value}
        </Typography>
    </Box>
)

export default PaymentConfirm
